import React from 'react';
import type {UIMessage} from 'ai';

/**
 * A signature that changes whenever a message's rendered content changes — growing
 * text, added parts, or a tool part changing state. Pass it as `resetKey` so the
 * boundary re-attempts the render on each content change: a transient throw on a
 * partial mid-stream chunk clears once a later chunk makes the content valid, instead
 * of pinning the message to the fallback forever.
 */
export function messageResetKey(message: UIMessage): string {
    return message.parts
        .map((p) => {
            if (p.type === 'text') return `t${p.text.length}`;
            const state = (p as {state?: string}).state;
            return state ? `${p.type}:${state}` : p.type;
        })
        .join('|');
}

interface Props {
    children: React.ReactNode;
    /** When this changes, a latched error is cleared and the children are re-attempted. */
    resetKey?: unknown;
}

interface State {
    error: Error | null;
}

/**
 * Contains a render failure to a SINGLE Ask Excelsior chat message.
 *
 * Without this, a throw while rendering one message (e.g. a CD-explainability
 * "investigation branch" whose content trips a `.split` on undefined inside the
 * markdown renderer) bubbles to the app-level FatalErrorPage and blanks the whole
 * page (seen at excelsior.amazon.com/forecast: "An error has occured — can't access
 * property split, e is undefined"). Scoping the boundary to each message keeps the
 * rest of the chat — and the surrounding page — alive, and logs the real (unminified
 * in dev) stack so the exact failing line can be pinned from the console.
 *
 * Unlike a bare class boundary, this resets when `resetKey` changes, so a message that
 * only briefly throws mid-stream still renders its final, valid content.
 */
export class MessageErrorBoundary extends React.Component<Props, State> {
    state: State = {error: null};

    static getDerivedStateFromError(error: Error): State {
        return {error};
    }

    componentDidUpdate(prevProps: Props) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({error: null});
        }
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // eslint-disable-next-line no-console
        console.error('[AskExcelsior] message render failed', error, info?.componentStack);
    }

    render() {
        if (this.state.error) {
            // This renders exactly when the message subtree just threw — possibly from
            // inside the UI toolkit itself — so the fallback is a plain element with no
            // component-library dependency, and can't re-enter the failing render path.
            // The design-token custom property themes it, with a hex fallback if unset.
            return (
                <div role="status" style={{color: 'var(--color-text-status-error, #d91515)', fontSize: 12, padding: '2px 0'}}>
                    This message couldn&apos;t be displayed. The rest of the chat is unaffected.
                </div>
            );
        }
        return <>{this.props.children}</>;
    }
}
